/**
 * The one way a web frontend reads its tier configuration (release flow v2,
 * D5: one image per app, promoted unchanged from staging to production).
 *
 *   - In a BUILT bundle — what an image serves — the values come from
 *     `window.__TONNIE_ENV__`, which `/env.js` sets. The container writes that
 *     file when it starts, from its environment and the app's allowlist
 *     (runtime-env-spec.ts), and `index.html` loads it before the bundle. The
 *     bundle itself carries no tier value.
 *   - On the dev servers (the E2E stack included) and in Vitest the values
 *     come from `import.meta.env`, as Vite gives them.
 *
 * Which one is Vite's to say, so each app binds its accessor once, in its own
 * `runtime-env.ts`:
 *
 *   createRuntimeEnv('journey-web',
 *     import.meta.env.PROD ? { built: true } : { built: false, devEnv: import.meta.env })
 *
 * In a build `import.meta.env.PROD` is the literal `true`, so the `devEnv`
 * branch — the only place `import.meta.env` is handed over whole — is dead
 * code the bundler drops: no build-time VITE_* value can reach a bundle
 * through here. The built-output gate (scripts/qa/check-built-assets.mjs)
 * builds every frontend with probe values in its environment and fails if one
 * appears in the output.
 */
import type { FrontendApp, RuntimeEnvKey } from './runtime-env-spec';

declare global {
  interface Window {
    /** Set by `/env.js`, which the image writes at container start. */
    __TONNIE_ENV__?: Readonly<Record<string, string>>;
  }
}

/** Where an app's values come from: the container's /env.js, or Vite's env. */
export type RuntimeEnvSource =
  | { readonly built: true }
  | { readonly built: false; readonly devEnv: Readonly<Record<string, unknown>> };

/** A built bundle ran without its `/env.js`: there is no configuration to read. */
export class RuntimeEnvNotLoadedError extends Error {
  constructor() {
    super(
      '[runtime-env] window.__TONNIE_ENV__ is not set: /env.js did not load before the bundle. ' +
        'A built frontend reads its tier configuration from the /env.js its container writes at start ' +
        '(docker/frontend/write-env-js.sh).'
    );
    this.name = 'RuntimeEnvNotLoadedError';
  }
}

export interface RuntimeEnv<A extends FrontendApp> {
  readonly app: A;
  /** The variable's value, or `undefined` when it is unset or empty. */
  get(key: RuntimeEnvKey<A>): string | undefined;
}

function valuesOf(source: RuntimeEnvSource): Readonly<Record<string, unknown>> {
  if (!source.built) return source.devEnv;
  const loaded = typeof window === 'undefined' ? undefined : window.__TONNIE_ENV__;
  if (!loaded) throw new RuntimeEnvNotLoadedError();
  return loaded;
}

/**
 * The accessor for one app. Each read looks the value up again (no cache), so
 * a test may stub the environment between reads.
 */
export function createRuntimeEnv<A extends FrontendApp>(
  app: A,
  source: RuntimeEnvSource
): RuntimeEnv<A> {
  return {
    app,
    get(key) {
      const value = valuesOf(source)[key];
      return typeof value === 'string' && value.length > 0 ? value : undefined;
    },
  };
}
